import { getSessionStorage } from '@omnia/ui/src/lib/utils/session/getSessionStorage'
import { setSessionStorage } from '@omnia/ui/src/lib/utils/session/setSessionStorage'
import { Workflow } from '@omnia/ui/src/lib/workflow/workflow'
import { getOrder } from '@vista-digital/occ-domain'
import { TicketingSubPages } from '@/common/services/models/pages'
import { navigationService } from '@/common/services/navigation.service'
import { TicketingWorkflowState } from './ticketing-workflow-state'

export class TicketingWorkflow extends Workflow<TicketingSubPages, TicketingWorkflowState> {
    public constructor() {
        super(TicketingSubPages)
        this.pages = ['SeatPicker', 'TicketingLogin', 'TicketPicker', 'OrderSummary', 'OrderPayment']

        this.stateStorageKey = 'ticketing-workflow-state'

        const sessionState = getSessionStorage<TicketingWorkflowState>(this.stateStorageKey)
        this.state = sessionState ?? new TicketingWorkflowState()

        // push `Concession` on index 3 page if it configured on the `CMS`
        if (navigationService.pageExist('Ticketing', 'Concessions') && this.state.isConcessionsEnabled) {
            this.pages.splice(3, 0, 'Concessions')
        }

        // If the workflow state is outdated, reset it
        if (this.state.orderId && !getOrder()) {
            this.updateState(_ => new TicketingWorkflowState())
        }
    }

    public inExactWorkflow(page: keyof TicketingSubPages, pathParams: { [key: string]: string }): boolean {
        const showtimeId = pathParams?.['showtimeId']
        if (showtimeId && (showtimeId === this.state.showtimeId || this.state.showtimeId === undefined)) {
            return this.inWorkflow(page)
        }

        return false
    }

    public inWorkflow(page: keyof TicketingSubPages) {
        return page === 'Concessions'
            ? !!this.state.isConcessionsEnabled && super.inWorkflow(page)
            : super.inWorkflow(page)
    }

    public updateState(updateState: (state: TicketingWorkflowState) => TicketingWorkflowState) {
        this.state = updateState(this.state)
        setSessionStorage(this.stateStorageKey, this.state)
    }

    public navigateBackToWorkflow(): boolean {
        if (this.state.currentPage && this.state.showtimeId) {
            return this.goToPage(this.state.currentPage, { showtimeId: this.state.showtimeId })
        }

        return false
    }

    public initWorkflowPages(isConcessionsEnabled: boolean) {
        this.updateState(s => ({ ...s, isConcessionsEnabled }))

        if (!navigationService.pageExist('Ticketing', 'Concessions')) return
        if (this.pages.includes('Concessions')) return
        if (!isConcessionsEnabled) return

        this.pages.splice(3, 0, 'Concessions')
    }

    public override end(): void {
        super.end()
        this.updateState(_ => new TicketingWorkflowState())
    }
}

export const ticketingWorkflow = new TicketingWorkflow()
